import { infringementsService } from '@/api/infringements.service';
import type {
  CreateInfringementDto,
  InfringementsControllerFindAllData,
  InfringementsControllerGetUserActionNeededCountData,
  UpdateInfringementDto,
} from '@carvia/ros-client-types';
import type { Infringement } from '@carvia/ros-client-types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationDefaultsContext } from './query-client';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';
import { computed, type MaybeRefOrGetter, toRef, toValue } from 'vue';

export const INFRINGEMENTS_QUERY_KEY = 'infringements';
export const INFRINGEMENT_USER_ACTION_NEEDED_COUNT_QUERY_KEY =
  'infringement-user-action-needed-count';

export const useInfringement = (id: MaybeRefOrGetter<string>) => {
  return useQuery({
    queryKey: [INFRINGEMENTS_QUERY_KEY, id],
    queryFn: () => infringementsService.getOne(toValue(id)),
  });
};

export const useInfringements = (
  query: MaybeRefOrGetter<InfringementsControllerFindAllData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [INFRINGEMENTS_QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      infringementsService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    enabled,
    getNextPageParam: (lastPage) => (lastPage.meta?.nextPage ? lastPage.meta.nextPage : undefined),
    initialPageParam: 1,
  });
};

export const useInfringementUserActionNeededCount = (
  query: MaybeRefOrGetter<InfringementsControllerGetUserActionNeededCountData['query']>,
) => {
  return useQuery({
    queryKey: computed(() => [
      INFRINGEMENT_USER_ACTION_NEEDED_COUNT_QUERY_KEY,
      { ...toValue(query) },
    ]),
    queryFn: () => infringementsService.getUserActionNeededCount(toValue(query)),
  });
};

export const useCreateInfringement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INFRINGEMENTS_QUERY_KEY],
    mutationFn: (infringement: CreateInfringementDto) => infringementsService.create(infringement),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Infringement created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      addItemToQueriesData<Infringement>([INFRINGEMENTS_QUERY_KEY], result, queryClient);
    },
  });
};

export const useExecuteInfringement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INFRINGEMENTS_QUERY_KEY, 'execute'],
    mutationFn: (id: string) => infringementsService.execute(id),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Infringement execution started',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<Infringement>([INFRINGEMENTS_QUERY_KEY], result, queryClient);
    },
  });
};

export type RetryableInfringementStep =
  | 'findBooking'
  | 'chargeFee'
  | 'sendPaymentLink'
  | 'sendInvoice'
  | 'sendAuthorityEmail';

export const useRetryInfringementStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INFRINGEMENTS_QUERY_KEY, 'retry-step'],
    mutationFn: ({ id, step }: { id: string; step: RetryableInfringementStep }) => {
      switch (step) {
        case 'findBooking':
          return infringementsService.retryFindBooking(id);
        case 'chargeFee':
        case 'sendPaymentLink':
        case 'sendInvoice':
          return infringementsService.retryChargeFee(id);
        case 'sendAuthorityEmail':
          return infringementsService.retrySendAuthorityEmail(id);
      }
    },
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Infringement retry started',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<Infringement>([INFRINGEMENTS_QUERY_KEY], result, queryClient);
    },
  });
};

export const useUpdateInfringement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INFRINGEMENTS_QUERY_KEY, 'update'],
    mutationFn: ({ id, payload }: { id: string; payload: UpdateInfringementDto }) =>
      infringementsService.update(id, payload),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Infringement updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<Infringement>([INFRINGEMENTS_QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteInfringement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [INFRINGEMENTS_QUERY_KEY, 'delete'],
    mutationFn: (id: string) => infringementsService.remove(id),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Infringement deleted',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      removeItemFromQueriesData<Infringement>([INFRINGEMENTS_QUERY_KEY], variables, queryClient);
    },
  });
};
